import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {RootState} from "../../store/store";
import {apiRequestWithAuth} from "../../utils/api";
import {Pencil, Trash2} from "lucide-react";
import Label from "../../components/form/Label";
import FileInput from "../../components/form/input/FileInput";
import PageMeta from "../../components/common/PageMeta";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import {EditIcon} from "../../icons";

type Category = {
    id: number;
    title: string;
    status: number;
    statusText: string;
    type: number;
    typeText: string;
    shortDescription: string;
    slug: string;
    parentId?: number;
    image?: number;
};

const CategoriesList = () => {
    const [categories, setCategories] = useState<Category[]>([]);
    const [title, setTitle] = useState("");
    const [slug, setSlug] = useState("");
    const [status, setStatus] = useState<number>(1);
    const [type, setType] = useState<number>(1);
    const [shortDescription, setShortDescription] = useState("");
    const [parentId, setParentId] = useState<number | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState<any>(null);
    const [image, setImage] = useState<number>(0);
    const [preview, setPreview] = useState<string | null>(null);

    const loadCategories = async () => {
        const res = await apiRequestWithAuth('/category', 'GET', {});
        setCategories(res.result);
    };

    const createCategories = async () => {
        const res = await apiRequestWithAuth('/category', 'POST', {
            title: title,
            slug: slug,
            parentId: parentId,
        });

        setCategories([...categories, res.result]);
    };

    useEffect(() => {
        if (selectedCategory) {
            setTitle(selectedCategory.title);
            setSlug(selectedCategory.slug);
            setStatus(selectedCategory.status);
            setType(selectedCategory.type);
            setShortDescription(selectedCategory.shortDescription);
            setParentId(selectedCategory.parentId);
        } else {
            resetFrom()
        }
    }, [selectedCategory]);

    useEffect(() => {
        loadCategories();
    }, []);

    const resetFrom = () => {
        setTitle("");
        setSlug("");
        setImage(0);
    }

    const handleCreate = async () => {
        if (!title || !slug) return;
        setIsLoading(true);
        try {
            let data: any = {
                title: title,
                slug: slug,
                shortDescription: shortDescription || '',
                parentId: parentId,
            }
            if (image > 0) {
                data.image = image;
            }
            const res = await apiRequestWithAuth('/category', 'POST', data);

            setCategories([...categories, res.result]);
            setTitle("");
            setSlug("");
            setStatus(1);
            setType(1);
            setImage(0);
            setShortDescription("");
            setParentId(undefined);
            setPreview(null);
        } catch (error) {
            console.error("Failed to create category:", error);
        } finally {
            setIsLoading(false);
        }

    };

    const handleUpdate = async () => {
        const categoryId = selectedCategory?.id;
        if (!title || !slug || !categoryId) return;
        setIsLoading(true);
        try {
            let data: any = {
                title: title,
                slug: slug,
                status: status,
                type: type,
                shortDescription: shortDescription || '',
                parentId: parentId,
            }
            if (image > 0) {
                data.image = image;
            }
            const res = await apiRequestWithAuth('/category/' + categoryId, 'PUT', data);

            setCategories((prev) =>
                prev.map((cat) => (cat.id === res.result.id ? res.result : cat))
            );
            setTitle("");
            setSlug("");
            setStatus(1);
            setType(1);
            setImage(0);
            setShortDescription("");
            setParentId(undefined);
            setSelectedCategory(null);
        } catch (error) {
            console.error("Failed to create category:", error);
        } finally {
            setIsLoading(false);
        }

    };

    const uploadFile = async (file: File) => {
        const formDataUpload = new FormData();
        formDataUpload.append("file", file);
        formDataUpload.append("id", "1");
        formDataUpload.append("subject", "category");

        try {
            const res = await apiRequestWithAuth("/media/file", "POST", formDataUpload);
            return res.result?.id || null;
        } catch (error) {
            console.error("Upload failed", error);
            return null;
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setPreview(URL.createObjectURL(file)); // Hiển thị ảnh preview
            const uploadedId = await uploadFile(file);
            if (uploadedId) {
                setImage(uploadedId);
            }
        }
    };

    const handleRemove = async (id: any) => {
        try {
            const res = await apiRequestWithAuth('/category/' + id, 'DELETE', {});

            setCategories((prev) =>
                prev.filter((node) => node.id !== id)
            );
        } catch (err) {
            console.error("Xóa thất bại:", err);
        }
    };

    const generateSlug = (text: string) =>
        text
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9\s-]/g, "")
            .replace(/\s+/g, "-");

    return (
        <>
            <div className="p-6">
                <PageMeta title="Categories" description="Trang tạo Categories"/>
                <PageBreadcrumb pageTitle="Categories"/>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-12 gap-6 max-w-10xl mx-auto">
                {/* Left: Form tạo mới */}
                <div className="md:col-span-4">
                    <div className="bg-white shadow rounded-lg p-6">
                        <h2 className="text-xl font-semibold mb-4">Tạo Category mới</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Tên</label>
                                <input
                                    type="text"
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                    value={title}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setTitle(value);
                                        setSlug(generateSlug(value));
                                    }}/>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Slug</label>
                                <input
                                    type="text"
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                    value={slug}
                                    onChange={(e) => setSlug(e.target.value)}/>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Mô tả</label>
                                <textarea
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                    value={shortDescription ?? ""}
                                    onChange={(e) => setShortDescription(e.target.value)}
                                    rows={3}/>
                            </div>
                            <div>
                                <Label className="block text-sm font-medium text-gray-700">Avatar</Label>
                                <FileInput onChange={handleFileChange}/>
                                {preview && (
                                    <div>
                                        <img src={preview} alt="Avatar Preview"
                                             className="w-32 h-32 object-cover rounded-full border"/>
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Category cha</label>
                                <select
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                    value={parentId ?? ""}
                                    onChange={(e) => setParentId(e.target.value ? Number(e.target.value) : undefined)}
                                >
                                    <option value="">-- Không chọn --</option>
                                    {categories.map((cat) => (
                                        <option key={cat.id} value={cat.id}>
                                            {cat.title}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <Label>Trạng thái</Label>
                                <select
                                    name="status"
                                    value={status}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setStatus(value ? parseInt(value) : 1);
                                    }}
                                    className="block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                >
                                    <option value="">-- Trạng thái --</option>
                                    <option value="1">Active</option>
                                    <option value="2">Draft</option>
                                    <option value="3">Inactive</option>
                                    <option value="4">Delete</option>
                                </select>
                            </div>
                            <div>
                                <Label>Type</Label>
                                <select
                                    name="type"
                                    value={type}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setType(value ? parseInt(value) : 1);
                                    }}
                                    className="block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                >
                                    <option value="">-- Loại --</option>
                                    <option value="1">Khóa học/bài kiểm tra</option>
                                    <option value="2">Bài viết</option>
                                </select>
                            </div>
                            {selectedCategory ? (
                                <button
                                    onClick={handleUpdate}
                                    disabled={isLoading}
                                    className="w-full bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700 transition disabled:opacity-50"
                                >
                                    {isLoading ? "Đang cập nhật..." : "Cập nhật"}
                                </button>
                            ) : (
                                <button
                                    onClick={handleCreate}
                                    disabled={isLoading}
                                    className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition disabled:opacity-50"
                                >
                                    {isLoading ? "Đang tạo..." : "Tạo mới"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right: Danh sách category */}
                <div className="md:col-span-8">
                    <div className="bg-white shadow rounded-lg p-6">
                        <h2 className="text-xl font-semibold mb-4">Danh sách Categories</h2>
                        <table className="w-full table-auto border-collapse">
                            <thead>
                            <tr className="bg-gray-100">
                                <th className="text-left py-2 px-4">Tên</th>
                                <th className="text-left py-2 px-4">Slug</th>
                                <th className="text-left py-2 px-4">Parent</th>
                                <th className="text-left py-2 px-4">Status</th>
                                <th className="text-left py-2 px-4">Type</th>
                                <th className="text-left py-2 px-4"></th>
                            </tr>
                            </thead>
                            <tbody>
                            {categories.length === 0 && (
                                <tr>
                                    <td colSpan={3} className="text-center py-4 text-gray-500">
                                        Chưa có category nào.
                                    </td>
                                </tr>
                            )}
                            {categories.map((cat) => (
                                <tr key={cat.id} className="border-b">
                                    <td className="py-2 px-4">{cat.title}</td>
                                    <td className="py-2 px-4 text-gray-600">{cat.slug}</td>
                                    <td className="py-2 px-4 text-gray-600">
                                        {categories.find((p) => p.id === cat.parentId)?.title || "-"}
                                    </td>
                                    <td className="py-2 px-4 text-gray-600">
                                        <span
                                            className={`inline-flex items-center px-2.5 py-0.5 justify-center gap-1 rounded-full font-medium text-theme-xs
                                                ${cat.statusText == 'Active' ? 'bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-500' : 'bg-error-50 text-error-600 dark:bg-error-500/15 dark:text-error-500'}`}>
                                          {cat.statusText}
                                        </span>
                                    </td>
                                    <td className="py-2 px-4">{cat.typeText}</td>
                                    <td className="py-2 px-4 text-gray-600 text-end">
                                        <button
                                            className="text-sm px-1.5 py-1.5 rounded-md bg-indigo-100 text-indigo-600 hover:bg-indigo-200 transition"
                                            onClick={() => setSelectedCategory(cat)}
                                        >
                                            <EditIcon/>
                                        </button>
                                        {/*<button
                className="text-red-500 ml-2"
                onClick={() => setConfirmDeleteId(cat.id)}
            >
                <Trash2 size={18} />
            </button>*/}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                {confirmDeleteId && (
                    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                        <div className="bg-white p-6 rounded shadow-md w-full max-w-md">
                            <h2 className="text-lg font-semibold mb-4">Xác nhận xoá</h2>
                            <p className="mb-6">Bạn có chắc chắn muốn xoá category này không?</p>
                            <div className="flex justify-end gap-3">
                                <button
                                    className="px-4 py-2 bg-gray-300 rounded"
                                    onClick={() => setConfirmDeleteId(null)}
                                >
                                    Huỷ
                                </button>
                                <button
                                    className="px-4 py-2 bg-red-500 text-white rounded"
                                    onClick={() => {
                                        handleRemove(confirmDeleteId);
                                        setConfirmDeleteId(null);
                                    }}
                                >
                                    Xoá
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default CategoriesList;
